import React, { useState } from 'react';

import {
  Alert,
  Box,
  Chip,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { type SavedLensProposal, useLensProposalVisualPreview } from '~/hooks/apis/reporting/useLensCollaboration';

import type { Lens } from '~/types/leLens';

import LensCardView from './cards/LensCardView';

export default function LensProposalVisualPreview({ lens, proposal }: { lens: Lens; proposal: SavedLensProposal }) {
  const cards = Array.from(
    new Map([...lens.spec.cards, ...proposal.definition.spec.cards].map((card) => [card.id, card])).values(),
  );
  const [cardId, setCardId] = useState(cards[0]?.id ?? '');
  const [mode, setMode] = useState<'compare' | 'before' | 'after'>('compare');
  const preview = useLensProposalVisualPreview(lens.id, lens.version, proposal.id, cardId);
  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2}>
        <TextField
          select
          size="small"
          label="Preview card"
          value={cardId}
          onChange={(event) => setCardId(event.target.value)}
          sx={{ minWidth: 240 }}
        >
          {cards.map((card) => (
            <MenuItem key={card.id} value={card.id}>
              {card.title}
            </MenuItem>
          ))}
        </TextField>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          onChange={(_, value) => value && setMode(value)}
          aria-label="Preview comparison"
        >
          <ToggleButton value="compare">Side by side</ToggleButton>
          <ToggleButton value="before">Before</ToggleButton>
          <ToggleButton value="after">After</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Live data, default filters.
      </Typography>
      {proposal.baseVersion !== lens.version && (
        <Alert severity="warning">
          This proposal is based on version {proposal.baseVersion}. Before shows the current version {lens.version}. It
          must be updated before approval.
        </Alert>
      )}
      {preview.error && <Alert severity="error">{preview.error.message}</Alert>}
      <Box
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: mode === 'compare' ? '1fr 1fr' : '1fr' }, gap: 2 }}
      >
        {(['before', 'after'] as const)
          .filter((side) => mode === 'compare' || mode === side)
          .map((side) => {
            const definition = side === 'before' ? lens : proposal.definition;
            const card = definition.spec.cards.find((entry) => entry.id === cardId);
            return (
              <Box
                key={side}
                sx={{
                  minWidth: 0,
                  border: '1px solid',
                  borderColor: side === 'after' ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1">{side === 'before' ? 'Before' : 'After'}</Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={side === 'before' ? `Current · v${lens.version}` : 'Proposed · not published'}
                  />
                </Stack>
                {card?.type === 'insight' && !card.insight?.content ? (
                  <Typography>This insight has not been generated.</Typography>
                ) : card ? (
                  <Box sx={{ height: 360, overflow: 'auto' }}>
                    <LensCardView
                      card={card}
                      data={preview.data?.[side] ?? undefined}
                      datasets={definition.spec.datasets ?? []}
                      canEdit={false}
                      isFetchingData={preview.isFetching}
                      dateRange={{}}
                      hasActiveFilters={false}
                      onClearFilters={() => {}}
                      onDelete={async () => {}}
                      onEdit={() => {}}
                      onRefresh={() => {}}
                      onSelect={() => {}}
                      isRefreshing={false}
                      insightRefresh={0}
                      span={8}
                    />
                  </Box>
                ) : (
                  <Typography color="text.secondary">{side === 'before' ? 'New card' : 'Card removed'}</Typography>
                )}
              </Box>
            );
          })}
      </Box>
    </Stack>
  );
}
